import { AlreadyEditingModal, IAlreadyEditingModalData } from './AlreadyEditingModal';
import { HistoryModal, IHistoryModalData } from './HistoryModal';
import { IChatModalData, ReportChatModal } from './ReportChatModal';
import { ISaveReportModalData, SaveReportModal } from './SaveReportModal';
import { ISaveReportNoticeModalData, SaveReportNoticeModal } from './SaveReportNoticeModal';
import React, { ReactNode, createContext, useContext, useState } from 'react';

import { CheckmateDialog } from '../../../../components/shared/dialog';
import { ClearSettingsConfirmModal } from './ClearSettingsConfirmModal';
import { CollabReportUnsavedChangesReason } from '../enums/CollabReportUnsavedChangesReason';
import { ExistingLeaseModal } from './ExistingLeaseModal';
import { UnsavedChangesDetectedModal } from './UnsavedChangesDetectedModal';

// Define a generic type for dialog context
interface CollabModalContextType {
    openModal<T, C>(modalType: CollabModalTypes, data?: T, onConfirm?: (data: C) => void): void;
    closeModal(modalType: CollabModalTypes): void;
    getModalState(modalType: CollabModalTypes): boolean;
    getModalConfirmCallback<C>(modalType: CollabModalTypes): ((data: C) => void) | undefined;
    getModalData<T>(modalType: CollabModalTypes): T | undefined;
    patchModalData<T>(modalType: CollabModalTypes, data: T): void;
}

const CollabModalContext = createContext<CollabModalContextType | undefined>(undefined);

export enum CollabModalTypes {
    AlreadyEditing,
    Chat,
    ClearSettingConfirm,
    ExistingLease,
    FailedToObtainLease,
    ForceReleaseConfirm,
    ForceReleaseSuccess,
    History,
    RemoveSection,
    Save,
    SaveNotice,
    UnsavedChangesDetected,
}

export interface ICollabModalProps<T, C> {
    open: boolean;
    onClose: () => void;
    onConfirm?: (data?: C) => void;
    data?: T;
}

export const CollabModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [modalStates, setModalStates] = useState<Record<string, boolean>>({});
    const [confirmCallbacks, setConfirmCallbacks] = useState<Record<string, any>>({});
    const [modalData, setModalData] = useState<Record<string, any>>({});

    const openModal = <T, C>(
        modalType: CollabModalTypes,
        data: T,
        onConfirm?: (data: C) => void
    ) => {
        setModalStates((prev) => ({ ...prev, [modalType]: true }));
        setConfirmCallbacks((prev) => ({ ...prev, [modalType]: onConfirm }));
        setModalData((prev) => ({ ...prev, [modalType]: data }));
    };

    const closeModal = (modalType: CollabModalTypes) => {
        setModalStates((prev) => ({ ...prev, [modalType]: false }));
        setConfirmCallbacks((prev) => ({ ...prev, [modalType]: undefined }));
        setModalData((prev) => ({ ...prev, [modalType]: undefined }));
    };

    const getModalState = (modalType: CollabModalTypes) => modalStates[modalType] || false;
    const getModalConfirmCallback = <C,>(modalType: CollabModalTypes) =>
        confirmCallbacks[modalType] as ((data?: C) => void) | undefined;
    const getModalData = <T,>(modalType: CollabModalTypes) => modalData[modalType] as T;
    const updateModalData = <T,>(modalType: CollabModalTypes, data: T) => {
        if (modalType in modalData) {
            const mergedData = { ...modalData[modalType], ...data };
            setModalData((prev) => ({
                ...prev,
                [modalType]: mergedData,
            }));
        }
    };

    return (
        <CollabModalContext.Provider
            value={{
                openModal,
                closeModal,
                getModalState,
                getModalConfirmCallback,
                getModalData,
                patchModalData: updateModalData,
            }}
        >
            {children}
            {/* <ExampleModal
                open={getModalState('ModalA')}
                onClose={() => closeModal('ModalA')}
                onConfirm={getModalConfirmCallback('ModalA')}
                data={getModalData<string[]>('ModalA')}
            /> */}
            <HistoryModal
                data={getModalData<IHistoryModalData>(CollabModalTypes.History)}
                open={getModalState(CollabModalTypes.History)}
                onClose={() => closeModal(CollabModalTypes.History)}
            />
            <ReportChatModal
                data={getModalData<IChatModalData>(CollabModalTypes.Chat)}
                open={getModalState(CollabModalTypes.Chat)}
                onClose={() => closeModal(CollabModalTypes.Chat)}
            />
            <SaveReportModal
                data={getModalData<ISaveReportModalData>(CollabModalTypes.Save)}
                open={getModalState(CollabModalTypes.Save)}
                onClose={() => closeModal(CollabModalTypes.Save)}
            />
            <UnsavedChangesDetectedModal
                data={getModalData<{ reason: CollabReportUnsavedChangesReason }>(
                    CollabModalTypes.UnsavedChangesDetected
                )}
                open={getModalState(CollabModalTypes.UnsavedChangesDetected)}
                onConfirm={getModalConfirmCallback(CollabModalTypes.UnsavedChangesDetected)}
                onClose={() => closeModal(CollabModalTypes.UnsavedChangesDetected)}
            />
            <ExistingLeaseModal
                data={getModalData<{ dialogText: string }>(CollabModalTypes.ExistingLease)}
                open={getModalState(CollabModalTypes.ExistingLease)}
                onClose={() => closeModal(CollabModalTypes.ExistingLease)}
            />
            <AlreadyEditingModal
                data={getModalData<IAlreadyEditingModalData>(CollabModalTypes.AlreadyEditing)}
                open={getModalState(CollabModalTypes.AlreadyEditing)}
                onClose={() => closeModal(CollabModalTypes.AlreadyEditing)}
            />
            <ClearSettingsConfirmModal
                open={getModalState(CollabModalTypes.ClearSettingConfirm)}
                onConfirm={getModalConfirmCallback(CollabModalTypes.ClearSettingConfirm)}
                onClose={() => closeModal(CollabModalTypes.ClearSettingConfirm)}
            />
            <SaveReportNoticeModal
                data={getModalData<ISaveReportNoticeModalData>(CollabModalTypes.SaveNotice)}
                open={getModalState(CollabModalTypes.SaveNotice)}
                onClose={() => closeModal(CollabModalTypes.SaveNotice)}
            />
            <CheckmateDialog
                body="The section you are trying to remove contains comments. Removing the section will delete the comment for the section. Are you sure you want to remove this section? "
                cancelText="No"
                closeButtonClassName="btn btn-default float-end"
                confirmButtonClassName="btn btn-black float-end"
                confirmText="Yes"
                dialogClassName="confirm-document-delete-dialog"
                handleClose={() => closeModal(CollabModalTypes.RemoveSection)}
                handleConfirm={getModalConfirmCallback(CollabModalTypes.RemoveSection)}
                isShowingModal={getModalState(CollabModalTypes.RemoveSection)}
            />
            <CheckmateDialog
                body={`The report can now be edited by other users.`}
                cancelText="Ok"
                closeButtonClassName="btn btn-default"
                handleClose={() => closeModal(CollabModalTypes.ForceReleaseSuccess)}
                isShowingModal={getModalState(CollabModalTypes.ForceReleaseSuccess)}
                showBackdrop
                size="sm"
                title={'Force Release Successful'}
            />
            <CheckmateDialog
                body={`Are you sure you wish to release this report to other users?\nThis cannot be undone.`}
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-orange"
                confirmText="Release"
                handleClose={() => closeModal(CollabModalTypes.ForceReleaseConfirm)}
                handleConfirm={getModalConfirmCallback(CollabModalTypes.ForceReleaseConfirm)}
                isShowingModal={getModalState(CollabModalTypes.ForceReleaseConfirm)}
                size="sm"
                title={'Force Release Confirmation'}
            />
        </CollabModalContext.Provider>
    );
};

export const useCollabReportModal = () => {
    const context = useContext(CollabModalContext);
    if (context === undefined) {
        throw new Error('useCollabReportModal must be used within a ModalProvider');
    }
    return context;
};
